import { Injectable } from "@angular/core";
import { DynamicTextContent } from "@odx/angular/cdk/dynamic-view";
import { SortStatus, TableSortVariant } from "@odx/angular/components/table";

@Injectable({
  providedIn: "root",
})
export class CloudProjectOdxTableService {
  sortData<T>(sortParams: SortStatus, totalData: T[]): T[] {
    if (!sortParams) return totalData;

    const { column, sortVariant } = sortParams;
    const key = column as keyof T;

    const sorted = [...totalData].sort((a, b) => {
      if (a[key] === b[key]) return 0;

      const asc = a[key] < b[key] ? -1 : 1;
      return sortVariant === TableSortVariant.ASC ? asc : -asc;
    });

    return sorted;
  }

  convertLastModifiedOn<
    T extends {
      lastModifiedOn: string;
    },
  >(data: T[]): T[] {
    return data.map((tableData) => {
      const convertedTableData: T = { ...tableData };
      if (!convertedTableData.lastModifiedOn.startsWith("2")) {
        convertedTableData.lastModifiedOn = "--";
        return convertedTableData;
      }
      const durationInDays = this.calculateDurationInDays(convertedTableData.lastModifiedOn);
      switch (true) {
        case durationInDays === 0:
          convertedTableData.lastModifiedOn = $localize`:@@global.today:heute`;
          break;
        case durationInDays === 1:
          convertedTableData.lastModifiedOn = $localize`:@@global.yesterday:gestern`;
          break;
        default:
          convertedTableData.lastModifiedOn = $localize`:@@cloudProjectsTable.lastModifiedDuration:vor ${durationInDays.toString()}:duration: Tagen`;
          break;
      }
      return convertedTableData;
    });
  }

  convertTimestamp(timestamp: string, localeId: string) {
    if (timestamp.startsWith("0")) {
      return "--";
    }
    const date = new Date(timestamp);
    return `${date.toLocaleDateString(localeId)}, ${date.toLocaleTimeString(localeId)}`;
  }

  filterData<T extends Record<string, any>>(filterParams: Record<string, string>, data: T[]): T[] {
    if (!filterParams || Object.values(filterParams).every((v) => !v || v.trim() === "")) {
      return data;
    }
    return data.filter((item: T) => {
      return Object.entries(filterParams).every(([key, filterParam]) => {
        const filterValue = filterParam.toLowerCase().trim();
        const value = item[key];
        if (value == null) return false;
        return value.toString().toLowerCase().includes(filterValue);
      });
    });
  }

  getSummaryContentFn(startItemIndex: number, endItemIndex: number, length: number): DynamicTextContent {
    const strongStart = "<strong>";
    const strongEnd = "</strong>";
    return $localize`:@@cloudProjectsTable.summaryContent:${strongStart}${startItemIndex}:startItemIndex:${strongEnd} bis ${strongStart}${endItemIndex}:endItemIndex:${strongEnd} von ${strongStart}${length}:length:${strongEnd}`;
  }

  getRangeContentFn(pageIndex: number, totalPages: number): DynamicTextContent {
    const strongStart = "<strong>";
    const strongEnd = "</strong>";
    return $localize`:@@cloudProjectsTable.pagination:Seite ${strongStart}${pageIndex + 1}:pageIndex:${strongEnd} von ${strongStart}${totalPages}:totalPages:${strongEnd}`;
  }

  private calculateDurationInDays(timestamp: string): number {
    const timeDifference: number = new Date().getTime() - new Date(timestamp).getTime();
    return Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  }
}
